import { useEffect, useMemo, useState } from 'react';
import { Conversations, type Conversation, type ConversationType } from '@/lib/api';
import { useChatStore } from '@/stores/chat';
import ConversationListItem from './ConversationListItem';
import NewChatModal from './NewChatModal';

interface Props {
  activeId: string | null;
  onSelect: (id: string) => void;
  /** Viewer id from AuthContext; fallback for the chat store's currentUserId. */
  selfUserId?: string | null;
}

export default function Sidebar({ activeId, onSelect, selfUserId = null }: Props) {
  const conversations = useChatStore((s) => s.conversations);
  const setConversations = useChatStore((s) => s.setConversations);
  const upsertConversation = useChatStore((s) => s.upsertConversation);
  const [query, setQuery] = useState('');
  const [showNew, setShowNew] = useState<ConversationType | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    Conversations.list()
      .then((res) => {
        if (cancelled) return;
        setConversations(res.conversations);
      })
      .catch((err) => {
        // The previous comment claimed this was surfaced via toast
        // elsewhere, but it wasn't — a broken API left the sidebar
        // empty with no indication. Surface through the same retry
        // banner the empty-state uses so the user can recover.
        if (cancelled) return;
        const msg = err && typeof err === 'object' && 'message' in err
          ? String((err as { message: unknown }).message)
          : 'Failed to load conversations';
        setLoadError(msg);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [setConversations]);

  const [loadError, setLoadError] = useState<string | null>(null);

  function handleRetry() {
    setLoading(true);
    setLoadError(null);
    Conversations.list()
      .then((res) => setConversations(res.conversations))
      .catch((err) => {
        const msg = err && typeof err === 'object' && 'message' in err
          ? String((err as { message: unknown }).message)
          : 'Failed to load conversations';
        setLoadError(msg);
      })
      .finally(() => setLoading(false));
  }

  const currentUserId = useChatStore((s) => s.currentUserId);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return conversations;
    return conversations.filter((c) => {
      // Match against the conversation's own name (groups have one, direct
      // chats historically had `name: null` and were unsearchable).
      if ((c.name ?? '').toLowerCase().includes(q)) return true;
      // Match against each member's displayName / username so direct chats
      // can be found by partner name, and groups can be found by member.
      const members = c.members ?? [];
      for (const m of members) {
        if (m.userId === currentUserId) continue;
        const u = m.user;
        if (!u) continue;
        if (
          (u.displayName ?? '').toLowerCase().includes(q) ||
          u.username.toLowerCase().includes(q)
        ) {
          return true;
        }
      }
      // Match against the last-message content / sender so users can
      // search inside their own history by keyword.
      const last = c.lastMessage;
      if (last?.content && last.content.toLowerCase().includes(q)) return true;
      if (last?.sender && (last.sender.displayName ?? '').toLowerCase().includes(q)) return true;
      return false;
    });
  }, [conversations, query, currentUserId]);

  return (
    <aside className="flex h-full w-full flex-col bg-white dark:bg-ink-800 md:w-80 md:border-r md:border-ink-100 md:dark:border-ink-700">
      <div className="flex items-center gap-2 px-3 pt-3">
        <input
          type="search"
          placeholder="Search chats"
          aria-label="Search chats"
          className="input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <button
          type="button"
          aria-label="New chat"
          title="New chat"
          className="btn-ghost px-2"
          onClick={() => setShowNew('direct')}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            <path d="M12 8v6M9 11h6" />
          </svg>
        </button>
        <button
          type="button"
          aria-label="New group"
          title="New group"
          className="btn-ghost px-2"
          onClick={() => setShowNew('group')}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
        </button>
      </div>

      <div className="nice-scroll mt-2 flex-1 overflow-y-auto px-2 pb-2" data-testid="conversation-list">
        {loadError && (
          <div
            role="alert"
            data-testid="sidebar-error"
            className="mx-2 mt-2 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
          >
            <span aria-hidden="true">⚠️</span>
            <span className="flex-1">{loadError}</span>
            <button type="button" className="btn-ghost px-2 py-0.5 text-xs" onClick={handleRetry}>
              Retry
            </button>
          </div>
        )}
        {loading && conversations.length === 0 && !loadError && (
          <div className="flex flex-col items-center justify-center gap-2 px-3 py-8 text-center text-sm text-ink-500 dark:text-ink-300">
            <span aria-hidden="true" className="text-2xl">⏳</span>
            <span className="text-xs">Loading conversations…</span>
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center gap-2 px-3 py-10 text-center text-sm text-ink-500 dark:text-ink-300">
            <span aria-hidden="true" className="text-3xl">{query ? '🔍' : '💬'}</span>
            {query ? (
              <>
                <span className="font-medium text-ink-700 dark:text-ink-100">No matches</span>
                <span className="text-xs">Try a different search.</span>
              </>
            ) : (
              <>
                <span className="font-medium text-ink-700 dark:text-ink-100">No conversations yet</span>
                <span className="text-xs">Start a new chat to get going.</span>
              </>
            )}
          </div>
        )}
        <ul className="space-y-1">
          {filtered.map((c) => (
            <li key={c.id}>
              <ConversationListItem
                conversation={c}
                active={c.id === activeId}
                onSelect={onSelect}
                selfUserId={selfUserId}
              />
            </li>
          ))}
        </ul>
      </div>

      {showNew && (
        <NewChatModal
          type={showNew}
          onClose={() => setShowNew(null)}
          onCreated={(c: Conversation) => {
            upsertConversation(c);
            setShowNew(null);
            onSelect(c.id);
          }}
        />
      )}
    </aside>
  );
}
